/**
 * My Objectives Service
 *
 * Service layer for interacting with the My Objectives state.
 * Provides methods for loading and managing objectives for students in the current user's caseload from Firestore.
 *
 * Objectives documents are stored at: partners/{partnerId}/objectives/{objectiveId}
 *
 * Real-time sync is maintained to keep the local state up to date with Firestore changes.
 */

import { Injectable, inject, OnDestroy } from '@angular/core';
import { Store, Select } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { MyObjectivesState, MyObjectivesStateModel } from './my-objectives.state';
import {
  Initialize,
  SetObjectives,
  SetInitialized,
  AddObjective,
  UpdateObjective,
  DeleteObjective,
  ClearMyObjectives,
} from './my-objectives.actions';
import { FirestoreService } from 'src/app/services/firestore/firestore';
import { Objective, ActivityType, ActivityCategory } from 'src/app/models/schema';
import { ActiveTeamState } from '../../account/active-team';
import { AuthState } from '../../../store/auth';
import { AccountDetailsState } from 'src/app/rxjs-store/account/account-details';
import { ProfileState } from 'src/app/rxjs-store/account/profile';
import { TeamActivityService } from 'src/app/rxjs-store/account/team-activity';
import { UtilsService } from 'src/app/services/utils.service';
import { Unsubscribe } from '@angular/fire/firestore';

/**
 * My Objectives Service
 *
 * Handles all my-objectives-related operations including loading from
 * and saving to Firestore with real-time sync support.
 */
@Injectable({
  providedIn: 'root',
})
export class MyObjectivesService implements OnDestroy {
  private store: Store = inject(Store);
  private firestoreService: FirestoreService = inject(FirestoreService);
  private utilsService: UtilsService = inject(UtilsService);
  private teamActivityService: TeamActivityService =
    inject(TeamActivityService);

  /**
   * Active objectives listener unsubscribe function
   */
  private objectivesUnsubscribe?: Unsubscribe;

  /**
   * Profile caseload subscription
   */
  private caseloadSubscription?: Subscription;

  /**
   * Cache of all objectives loaded from Firestore (before filtering)
   */
  private allObjectivesCache: Objective[] = [];

  // ========================================
  // Observable Selectors
  // ========================================

  /**
   * Observable of the entire my objectives state
   */
  @Select(MyObjectivesState.getState)
  myObjectivesState$!: Observable<MyObjectivesStateModel>;

  /**
   * Observable of all objectives
   */
  @Select(MyObjectivesState.objectives)
  objectives$!: Observable<Objective[]>;

  /**
   * Observable of objective IDs
   */
  @Select(MyObjectivesState.objectiveIds)
  objectiveIds$!: Observable<string[]>;

  /**
   * Observable of objective count
   */
  @Select(MyObjectivesState.objectiveCount)
  objectiveCount$!: Observable<number>;

  /**
   * Observable of initialized state
   */
  @Select(MyObjectivesState.initialized)
  initialized$!: Observable<boolean>;

  /**
   * Observable of whether there are objectives
   */
  @Select(MyObjectivesState.hasObjectives)
  hasObjectives$!: Observable<boolean>;

  /**
   * Observable of completed objectives
   */
  @Select(MyObjectivesState.completedObjectives)
  completedObjectives$!: Observable<Objective[]>;

  /**
   * Observable of active objectives
   */
  @Select(MyObjectivesState.activeObjectives)
  activeObjectives$!: Observable<Objective[]>;

  /**
   * Observable of objectives sorted by date
   */
  @Select(MyObjectivesState.objectivesSortedByDate)
  objectivesSortedByDate$!: Observable<Objective[]>;

  /**
   * Observable of objectives sorted by target date
   */
  @Select(MyObjectivesState.objectivesSortedByTargetDate)
  objectivesSortedByTargetDate$!: Observable<Objective[]>;

  // ========================================
  // Synchronous Getters
  // ========================================

  /**
   * Get all objectives (synchronous)
   */
  get objectives(): Objective[] {
    return this.store.selectSnapshot(MyObjectivesState.objectives);
  }

  /**
   * Get objective IDs (synchronous)
   */
  get objectiveIds(): string[] {
    return this.store.selectSnapshot(MyObjectivesState.objectiveIds);
  }

  /**
   * Get objective count (synchronous)
   */
  get objectiveCount(): number {
    return this.store.selectSnapshot(MyObjectivesState.objectiveCount);
  }

  /**
   * Get initialized state (synchronous)
   */
  get isInitialized(): boolean {
    return this.store.selectSnapshot(MyObjectivesState.initialized);
  }

  /**
   * Check if there are objectives (synchronous)
   */
  get hasObjectives(): boolean {
    return this.store.selectSnapshot(MyObjectivesState.hasObjectives);
  }

  /**
   * Get completed objectives (synchronous)
   */
  get completedObjectives(): Objective[] {
    return this.store.selectSnapshot(MyObjectivesState.completedObjectives);
  }

  /**
   * Get active objectives (synchronous)
   */
  get activeObjectives(): Objective[] {
    return this.store.selectSnapshot(MyObjectivesState.activeObjectives);
  }

  /**
   * Get a specific objective by ID (synchronous)
   */
  getObjective(id: string): Objective | null {
    return this.store.selectSnapshot(MyObjectivesState.objective(id));
  }

  /**
   * Get objectives by student ID (synchronous)
   */
  getObjectivesByStudentId(studentId: string): Objective[] {
    return this.store.selectSnapshot(MyObjectivesState.objectivesByStudentId(studentId));
  }

  /**
   * Get objectives by goal ID (synchronous)
   */
  getObjectivesByGoalId(goalId: string): Objective[] {
    return this.store.selectSnapshot(MyObjectivesState.objectivesByGoalId(goalId));
  }

  /**
   * Get objectives by skill ID (synchronous)
   */
  getObjectivesBySkillId(skillId: string): Objective[] {
    return this.store.selectSnapshot(MyObjectivesState.objectivesBySkillId(skillId));
  }

  // ========================================
  // Helper Methods
  // ========================================

  /**
   * Convert undefined values to null for Firestore compatibility
   * Firestore doesn't support undefined values, so we convert them to null
   */
  private convertUndefinedToNull<T extends Record<string, any>>(obj: T): T {
    const result: any = {};
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        if (value === undefined) {
          result[key] = null;
        } else if (
          value !== null &&
          typeof value === 'object' &&
          !Array.isArray(value)
        ) {
          // Recursively handle nested objects
          result[key] = this.convertUndefinedToNull(value);
        } else {
          result[key] = value;
        }
      }
    }
    return result as T;
  }

  // ========================================
  // Service Methods
  // ========================================

  /**
   * Initialize my objectives service
   *
   * Loads objectives for students in the active user's caseload and sets up real-time sync.
   * If the user has no caseload or an empty caseload, sets initialized to true
   * with an empty objectives array.
   * 
   * Subscribes to both:
   * 1. Objectives collection changes (real-time updates to objective data)
   * 2. Profile caseload changes (real-time updates to which objectives to show)
   */
  async initialize(): Promise<void> {
    // Dispatch initialize action
    this.store.dispatch(new Initialize());

    // Clean up existing listeners if any
    if (this.objectivesUnsubscribe) {
      this.objectivesUnsubscribe();
      this.objectivesUnsubscribe = undefined;
    }
    if (this.caseloadSubscription) {
      this.caseloadSubscription.unsubscribe();
      this.caseloadSubscription = undefined;
    }

    // Get active team
    const activeTeamId = this.store.selectSnapshot(ActiveTeamState.activeTeamId);
    if (!activeTeamId) {
      console.warn('[MyObjectivesService] No active team, setting initialized with empty objectives');
      this.store.dispatch(new SetObjectives([]));
      this.store.dispatch(new SetInitialized(true));
      return;
    }

    // Subscribe to profile caseload changes for real-time filtering
    this.caseloadSubscription = this.store
      .select(ProfileState.profile)
      .pipe(distinctUntilChanged((prev, curr) => {
        // Only re-filter if caseload actually changed
        const prevCaseload = prev?.caseload || [];
        const currCaseload = curr?.caseload || [];
        return JSON.stringify(prevCaseload.sort()) === JSON.stringify(currCaseload.sort());
      }))
      .subscribe((profile) => {
        const caseload = profile?.caseload || [];
        
        // Re-filter cached objectives with new caseload
        this.filterAndUpdateObjectives(caseload);
      });

    // Get initial caseload
    const profile = this.store.selectSnapshot(ProfileState.profile);
    const initialCaseload = profile?.caseload || [];

    // If no caseload or empty caseload, set initialized with empty objectives
    if (!initialCaseload || initialCaseload.length === 0) {
      this.store.dispatch(new SetObjectives([]));
      this.store.dispatch(new SetInitialized(true));
      // Still set up objectives listener so we can react if caseload gets populated later
    }

    return new Promise((resolve, reject) => {
      let isInitialLoad = true;

      try {
        // Collection path
        const collectionPath = `partners/${activeTeamId}/objectives`;

        // Set up real-time listener for all objectives (no where clause as per requirements)
        this.objectivesUnsubscribe =
          this.firestoreService.onCollectionChange<Objective>(
            collectionPath,
            (allObjectives) => {
              // Cache all objectives
              this.allObjectivesCache = allObjectives;
              
              // Get current caseload from state
              const currentProfile = this.store.selectSnapshot(ProfileState.profile);
              const currentCaseload = currentProfile?.caseload || [];

              // Filter objectives by current caseload
              this.filterAndUpdateObjectives(currentCaseload);

              // Resolve promise on initial load
              if (isInitialLoad) {
                isInitialLoad = false;
                resolve();
              }
            },
            {} // No query options - load all objectives
          );
      } catch (error: any) {
        const errorMessage = error.message || 'Failed to load my objectives';
        console.error(
          '[MyObjectivesService] Error loading my objectives:',
          errorMessage
        );
        // Still set initialized even on error
        this.store.dispatch(new SetObjectives([]));
        this.store.dispatch(new SetInitialized(true));
        reject(error);
      }
    });
  }

  /**
   * Filter cached objectives by caseload and update state
   * 
   * Objectives are filtered by checking if objective.studentId is in the caseload array
   * 
   * @param caseload - Array of student IDs to include
   */
  private filterAndUpdateObjectives(caseload: string[]): void {
    // Filter cached objectives by caseload student IDs (client-side filtering)
    const filteredObjectives = this.allObjectivesCache.filter((objective) => {
      return objective.studentId && caseload.includes(objective.studentId);
    });

    // Dispatch success action
    this.store.dispatch(new SetObjectives(filteredObjectives));
    this.store.dispatch(new SetInitialized(true));
  }

  /**
   * Create a new objective in Firestore
   *
   * @param objectiveData - Partial objective data to create
   * @returns Promise that resolves with the created objective ID
   */
  async createObjective(objectiveData: Partial<Objective>): Promise<string> {
    // Validate required fields
    if (
      !objectiveData.studentId ||
      !objectiveData.majorSkillAreaId ||
      !objectiveData.subsetSkillAreaId ||
      !objectiveData.skillId ||
      !objectiveData.measurmentType ||
      objectiveData.baselineValue === undefined ||
      objectiveData.targetValue === undefined ||
      !objectiveData.targetDate
    ) {
      throw new Error(
        'Missing required fields: studentId, majorSkillAreaId, subsetSkillAreaId, skillId, measurmentType, baselineValue, targetValue, or targetDate'
      );
    }

    const activeTeamId = this.store.selectSnapshot(ActiveTeamState.activeTeamId);
    if (!activeTeamId) {
      throw new Error('No active team selected');
    }

    const currentUserEmail =
      this.store.selectSnapshot(AuthState.userEmail) || 'unknown';
    const currentUserId =
      this.store.selectSnapshot(AuthState.userId) || 'unknown';
    const currentAccount = this.store.selectSnapshot(
      AccountDetailsState.account
    );
    const currentUserFirstName = currentAccount?.firstName || 'unknown';
    const currentUserLastName = currentAccount?.lastName || 'unknown';
    const now = Date.now();

    // Generate unique ID for objective
    const generatedId = this.utilsService.generateId();

    // Create complete objective object
    const objectiveToCreate: Partial<Objective> = {
      id: generatedId,
      studentId: objectiveData.studentId,
      majorSkillAreaId: objectiveData.majorSkillAreaId,
      subsetSkillAreaId: objectiveData.subsetSkillAreaId,
      skillId: objectiveData.skillId,
      goalId: objectiveData.goalId,
      measurmentType: objectiveData.measurmentType,
      baselineValue: objectiveData.baselineValue,
      targetValue: objectiveData.targetValue,
      targetDate: objectiveData.targetDate,
      completed: objectiveData.completed || false,
      completedAt: objectiveData.completedAt,
      completedBy: objectiveData.completedBy,
      createdAt: now,
      createdBy: currentUserEmail,
      updatedAt: now,
      updatedBy: currentUserEmail,
    };

    // Convert undefined to null for Firestore
    const cleanedObjective = this.convertUndefinedToNull(objectiveToCreate);

    // Optimistically add to local state
    this.store.dispatch(new AddObjective(cleanedObjective as Objective));

    try {
      // Save to Firestore
      const documentPath = `partners/${activeTeamId}/objectives/${generatedId}`;
      await this.firestoreService.setDocumentAsync(
        documentPath,
        cleanedObjective,
        true
      );

      // Log activity
      await this.teamActivityService.addActivity(activeTeamId, {
        uid: currentUserId,
        email: currentUserEmail,
        firstName: currentUserFirstName,
        lastName: currentUserLastName,
        category: ActivityCategory.TEAM,
        activityType: ActivityType.CREATED_STUDENT, // You may want a CREATED_OBJECTIVE type
        details: {
          personalNarrative: `You created an objective for student ${objectiveData.studentId}`,
          teamNarrative: `${currentUserEmail} created an objective for student ${objectiveData.studentId}`,
          objectiveId: generatedId,
          studentId: objectiveData.studentId,
        },
      });

      return generatedId;
    } catch (error: any) {
      // Revert optimistic update on error
      this.store.dispatch(new DeleteObjective(generatedId));
      console.error('[MyObjectivesService] Failed to create objective:', error);
      throw error;
    }
  }

  /**
   * Update an existing objective in Firestore
   *
   * @param objectiveId - Objective ID to update
   * @param updates - Partial objective data to update
   */
  async updateObjectiveInFirestore(
    objectiveId: string,
    updates: Partial<Objective>
  ): Promise<void> {
    const activeTeamId = this.store.selectSnapshot(ActiveTeamState.activeTeamId);
    if (!activeTeamId) {
      throw new Error('No active team selected');
    }

    const currentUserEmail =
      this.store.selectSnapshot(AuthState.userEmail) || 'unknown';
    const now = Date.now();

    // Add metadata
    const updatesToSave = {
      ...updates,
      updatedAt: now,
      updatedBy: currentUserEmail,
    };

    // Convert undefined to null for Firestore
    const cleanedUpdates = this.convertUndefinedToNull(updatesToSave);

    // Optimistically update local state
    this.store.dispatch(new UpdateObjective(objectiveId, cleanedUpdates));

    try {
      // Update in Firestore
      const documentPath = `partners/${activeTeamId}/objectives/${objectiveId}`;
      await this.firestoreService.updateDocumentAsync(
        documentPath,
        cleanedUpdates
      );
    } catch (error: any) {
      console.error('[MyObjectivesService] Failed to update objective:', error);
      // Note: Real-time listener will revert the optimistic update
      throw error;
    }
  }

  /**
   * Delete an objective from Firestore
   *
   * @param objectiveId - Objective ID to delete
   */
  async deleteObjectiveFromFirestore(objectiveId: string): Promise<void> {
    const activeTeamId = this.store.selectSnapshot(ActiveTeamState.activeTeamId);
    if (!activeTeamId) {
      throw new Error('No active team selected');
    }

    const currentUserId =
      this.store.selectSnapshot(AuthState.userId) || 'unknown';
    const currentUserEmail =
      this.store.selectSnapshot(AuthState.userEmail) || 'unknown';
    const currentAccount = this.store.selectSnapshot(
      AccountDetailsState.account
    );
    const currentUserFirstName = currentAccount?.firstName || 'unknown';
    const currentUserLastName = currentAccount?.lastName || 'unknown';

    // Get objective data before deletion for activity log
    const objective = this.getObjective(objectiveId);

    // Optimistically delete from local state
    this.store.dispatch(new DeleteObjective(objectiveId));

    try {
      // Delete from Firestore
      const documentPath = `partners/${activeTeamId}/objectives/${objectiveId}`;
      await this.firestoreService.deleteDocumentAsync(documentPath);

      // Log activity if we have objective data
      if (objective) {
        await this.teamActivityService.addActivity(activeTeamId, {
          uid: currentUserId,
          email: currentUserEmail,
          firstName: currentUserFirstName,
          lastName: currentUserLastName,
          category: ActivityCategory.TEAM,
          activityType: ActivityType.CREATED_STUDENT, // You may want a DELETED_OBJECTIVE type
          details: {
            personalNarrative: `You deleted an objective for student ${objective.studentId}`,
            teamNarrative: `${currentUserEmail} deleted an objective for student ${objective.studentId}`,
            objectiveId: objectiveId,
            studentId: objective.studentId,
          },
        });
      }

      console.log(`[MyObjectivesService] Deleted objective ${objectiveId}`);
    } catch (error: any) {
      // Revert optimistic update on error (re-add objective)
      if (objective) {
        this.store.dispatch(new AddObjective(objective));
      }
      console.error('[MyObjectivesService] Failed to delete objective:', error);
      throw error;
    }
  }

  /**
   * Clear all my objectives
   */
  clearMyObjectives(): void {
    // Clean up listeners
    if (this.objectivesUnsubscribe) {
      this.objectivesUnsubscribe();
      this.objectivesUnsubscribe = undefined;
    }
    if (this.caseloadSubscription) {
      this.caseloadSubscription.unsubscribe();
      this.caseloadSubscription = undefined;
    }

    // Clear cache
    this.allObjectivesCache = [];

    // Dispatch clear action
    this.store.dispatch(new ClearMyObjectives());
  }

  /**
   * Cleanup on service destruction
   */
  ngOnDestroy(): void {
    if (this.objectivesUnsubscribe) {
      this.objectivesUnsubscribe();
      this.objectivesUnsubscribe = undefined;
    }
    if (this.caseloadSubscription) {
      this.caseloadSubscription.unsubscribe();
      this.caseloadSubscription = undefined;
    }
    this.allObjectivesCache = [];
  }
}
